import {Injectable} from "@angular/core";
import {AddressModel, addressModelFactory} from "../address/address.model";
import {
  ItsmProjectSettableAddressesModel,
  itsmProjectSettableAddressesModelFactory,
  itsmSettableAddressesModelFactory,
  ItsmSiteSettableAddressesModel,
} from "../address/itsm-address.model";
import {ItsmContactModel, itsmContactModelListFactory} from "../contact/itsm-contact.model";
import {paramsToQueryString} from "../http/url-params-serializer";
import {SiteDetailModel, siteDetailModelFactory, siteDetailModelListFactory} from "./site-detail.model";
import {SiteModel} from "./site.model";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {ITSMSiteModel} from "./itsm-site.model";
import {AppSettingsService} from "../app-settings/app-settings.service";
import { Observable } from "rxjs";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Injectable()
export class SiteService {
  private url: string = "/api/sites";

  constructor(private http: HttpClient, private appSettingsService: AppSettingsService) {}

  getSites(
    page = DEFAULT_PAGE,
    limit = DEFAULT_PAGE_LIMIT,
    search: string,
    projectId: string,
    searchSerialNumber: boolean,
  ): Promise<{rows: SiteDetailModel[], count: number}> {
    const params = {
      page: `${page}`,
      limit: `${limit}`,
      search: `${search || ""}`,
      projectId: `${projectId || ""}`,
      sequence: "true",
      searchSerialNumber: `${searchSerialNumber || "false"}`,
    };

    return this.http.get<{rows: SiteDetailModel[], count: number}>(`${this.url}`, {params})
      .pipe(map(({rows, count}) => ({rows: siteDetailModelListFactory(rows), count: +count})))
      .toPromise();
  }

  getSitesByProjectId(projectId: string): Promise<any[]> {
    return this.http.get<any[]>(`${this.url}/getlistbyproject`, {params: {projectId}})
      .toPromise();
  }

  getSiteById(id: string): Promise<SiteDetailModel> {
    return this.http.get<{rows: SiteDetailModel[]}>(`${this.url}`, {params: {id}})
      .pipe(map(json => siteDetailModelFactory(json.rows[0])))
      .toPromise();
  }

  saveSite(site: SiteModel): Promise<boolean> {
    return this.http.post(`${this.url}/save`, {...site})
      .toPromise()
      .then(() => true);
  }

  getAddress(siteId: string, type: string): Promise<AddressModel> {
    const params = paramsToQueryString({siteId, type});
    return this.http.get<{ siteAddress: AddressModel }>(`${this.url}/address?${params}`)
      .pipe(map(({siteAddress}) => addressModelFactory(siteAddress)))
      .toPromise();
  }

  getItsmSettableAddresses(siteId: string): Promise<ItsmSiteSettableAddressesModel> {
    const params = paramsToQueryString({siteId});
    return this.http.get(`${this.url}/get-settable-itsm-addresses?${params}`)
      .pipe(map(itsmSettableAddressesModelFactory))
      .toPromise();
  }

  getItsmSettableAddressesByProject(projectId: string): Promise<ItsmProjectSettableAddressesModel> {
    const params = paramsToQueryString({projectId});
    return this.http.get(`${this.url}/get-settable-itsm-addr-by-project?${params}`)
      .pipe(map(itsmProjectSettableAddressesModelFactory))
      .toPromise();
  }

  remove(id: string): Promise<boolean> {
    return this.http.get(`${this.url}/remove`, {params: {id}})
      .toPromise()
      .then(() => true);
  }

  async loadSitesFromITSMToImport(projectId: string): Promise<ITSMSiteModel[]> {
    let siteQueryParams = { projectId };
    return this.http.get<{ sites: ITSMSiteModel[] }>(`${this.url}/get-itsm-sites-for-import`, { params: siteQueryParams })
      .pipe(map(json => ITSMSiteModel.fromList(json.sites)))
      .toPromise();
  }

  insertITSMSites(projectId: string, sites: any[]): Promise<number> {
    return this.http.post<{ count: number }>(`${this.url}/insert-itsm-sites`, {projectId, sites})
      .pipe(map(json => +json.count))
      .toPromise();
  }

  insertHasuraSites(body: any): Promise<any> {
    return this.http.post<any>(`${this.url}/insert-hasura-sites`, body)
      .toPromise();
  }

  getItsmContacts(siteId: string): Promise<ItsmContactModel[]> {
    return this.http.get(`${this.url}/get-itsm-contacts`, {params: {siteId}})
      .pipe(map((itsmContactModelListFactory)))
      .toPromise();
  }
  updateContractualContacts(projectId: string): Promise<any> {
    return this.http.post(`${this.url}/update-contractual-contacts`, {projectId})
      .toPromise();
  }

  exportPartnerInstallationFile(projectId: string): Promise<any> {
    return this.http.get(`${this.url}/export-multi-site-design-file`, { params: { projectId } }).toPromise();
  }
  listenToTransactionStatus(projectId): Observable<any> {
    return this.http.post<any>(`${this.url}/listen-to-transaction`, {projectId});
  }
}
